// src/context/UserContext.js
import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const UserContext = createContext(null);

// ── Axios interceptor: attach JWT to every request ──
const token = localStorage.getItem("globalplace_token");
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

function setAxiosToken(t) {
  if (t) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    localStorage.setItem("globalplace_token", t);
  } else {
    delete axios.defaults.headers.common["Authorization"];
    localStorage.removeItem("globalplace_token");
  }
}

// ── 401 response → auto logout (skip auth endpoints) ──
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const url = err.config?.url || "";
      const isAuthEndpoint = url.includes("/users/login") || url.includes("/users/register") || url.includes("/users/google-login");
      if (!isAuthEndpoint) {
        setAxiosToken(null);
        localStorage.removeItem("globalplace_user");
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

export function UserProvider({ children }) {
  const [user, setUser] = useState(null); // { user_id, nickname, instagram_url, is_public }
  const [loading, setLoading] = useState(true);

  // Restore user from localStorage on app start
  useEffect(() => {
    const saved = localStorage.getItem("globalplace_user");
    const savedToken = localStorage.getItem("globalplace_token");
    if (saved && savedToken) {
      try {
        setUser(JSON.parse(saved));
      } catch (e) {
        localStorage.removeItem("globalplace_user");
        setAxiosToken(null);
      }
    } else if (saved && !savedToken) {
      // Token missing (e.g. after JWT migration) — force re-login
      localStorage.removeItem("globalplace_user");
    }
    setLoading(false);
  }, []);

  const _saveUser = (userData) => {
    const { access_token, ...profile } = userData;
    setAxiosToken(access_token);
    setUser(profile);
    localStorage.setItem("globalplace_user", JSON.stringify(profile));
    return profile;
  };

  const login = async (nickname, pin) => {
    const res = await axios.post(`${API_BASE}/users/login`, { nickname, pin });
    return _saveUser(res.data);
  };

  const register = async (nickname, pin) => {
    const res = await axios.post(`${API_BASE}/users/register`, { nickname, pin });
    return _saveUser(res.data);
  };

  const googleLogin = async (idToken) => {
    const res = await axios.post(`${API_BASE}/users/google-login`, { id_token: idToken });
    return _saveUser(res.data);
  };

  const logout = () => {
    setUser(null);
    setAxiosToken(null);
    localStorage.removeItem("globalplace_user");
  };

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    // Don't store access_token in user state
    const { access_token, ...profile } = updated;
    if (access_token) setAxiosToken(access_token);
    setUser(profile);
    localStorage.setItem("globalplace_user", JSON.stringify(profile));
  };

  return (
    <UserContext.Provider value={{ user, loading, login, register, googleLogin, logout, updateUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}

export { API_BASE };
